/**
 * Show list of promo Code .
 *
 * @param {string | Date} startDate - The start date of the promotional campaign.
 * @param {string | Date} endDate - The end date of the promotional campaign.
 * @param {number} repetitions - The number of times the promo code can be used.
 * @param {number} discountAmount - The amount or percentage of the discount.
 *
 * Author: Khaliq Mehmood
 * Date:   22 sep, 2023
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";

import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import {
  getPromoCode,
  deletePromoCodeById,
  sendPromoEmail,
} from "../../actions/promoCodePageAction";
import resetBtn from "../../assets/images/reset-arrow.svg";

import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
import DataTable, { createTheme } from "react-data-table-component";
import InitializeLocalStorage from "../../components/PaginationHandle";

createTheme(
  "solarized",
  {
    text: {
      primary: "#000",
      secondary: "#000",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#cb4b16",
      text: "#FFFFFF",
    },
    divider: {
      default: "#DFE0EB",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const PromoIndex = () => {
  //use State Function To handle Data
  const [promoCodeData, setPromoCodeData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [getDelete, setDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [filterText, setFilterText] = useState("");
  const [selectedOption, setSelectedOption] = useState();
  const [filteredItems, setFilteredItems] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const location = useLocation();
  const { pathname } = location;

  InitializeLocalStorage(pathname, localStorage.getItem("currentURL"));

  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin.role;

  // useEffect to fetech all list of promo codes
  useEffect(() => {
    getAllPromoCode();
  }, []);

  const hideModel = () => {
    setDelete(false);
  };

  const handlePageChange = (page) => {
    getAllPromoCode(page);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    getAllPromoCode(page, newPerPage);
    setPerPage(newPerPage);
  };

  //function to delete promo Code
  const deleteMedicalType = async () => {
    setDelete(false);
    setLoader(true);
    let res = await deletePromoCodeById(deleteId);

    if (res.data.success) {
      toast.success(res.data.message);
      getAllPromoCode();
    }
    setLoader(false);
  };

  const showModel = (id) => {
    setDeleteId(id);
    setDelete(true);
  };

  //list to show Active promo Code
  const getAllPromoCode = async (page, limit = perPage) => {
    setLoader(true);
    let obj = { page, limit };
    let res = await getPromoCode(obj);
    if (res?.data?.data?.promoCode) {
      setTotalRows(res?.data?.data?.pagination?.total);
      setPromoCodeData(res?.data?.data?.promoCode);
    }
    setLoader(false);
  };

  const caseInsensitiveSort = (rowA, rowB, columnId) => {
    const getProperty = (row, columnId) => {
      // Helper function to get the property value based on the columnId
      switch (columnId) {
        case "Name Of Token":
          return row.name?.toLowerCase() || "";
        case "Discount Type":
          return row.discountType?.toLowerCase() || "";
        case "Discount Value":
          return String(row.discountValue).toLowerCase() || "";
        case "Start Date":
          const startDate = new Date(row.startDate);
          return `${
            startDate.getMonth() + 1
          }/${startDate.getDate()}/${startDate.getFullYear()}`.toLowerCase();
        case "End Date":
          const endDate = new Date(row.endDate);
          return `${
            endDate.getMonth() + 1
          }/${endDate.getDate()}/${endDate.getFullYear()}`.toLowerCase();
        case "Status":
          return row?.status || "";
        default:
          return "";
      }
    };

    const a = getProperty(rowA, columnId);
    const b = getProperty(rowB, columnId);

    if (a > b) {
      return 1;
    }
    if (b > a) {
      return -1;
    }
    return 0;
  };

  const handleSendEmail = async (id) => {
    setLoader(true);
    sendPromoEmail(id)
      .then((res) => {
        toast.success(res?.data?.message);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  //data Table to show indexing
  const columns = [
    {
      name: "Name Of Token",
      selector: (row) => row.name,
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Name Of Token"),
    },
    {
      name: "Discount Type",
      selector: (row) => row.discountType,
      sortable: true,
      sortFunction: caseInsensitiveSort,
    },
    {
      name: "Discount Value",
      selector: (row) => row.discountValue,
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Discount Type"),
    },
    {
      name: "Start Date",
      selector: (row) => {
        const startDate = new Date(row.startDate);
        return `${
          startDate.getMonth() + 1
        }/${startDate.getDate()}/${startDate.getFullYear()}`;
      },
      sortable: true,

      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Start Date"),
    },

    {
      name: "End Date",
      selector: (row) => {
        const endDate = new Date(row.endDate);
        return `${
          endDate.getMonth() + 1
        }/${endDate.getDate()}/${endDate.getFullYear()}`;
      },
      sortable: true,

      sortFunction: (a, b) => caseInsensitiveSort(a, b, "End Date"),
    },

    {
      name: "Status",
      selector: (row) => (
        <>
          {" "}
          {row.status ? (
            <label className="label label-success m-0">Active</label>
          ) : (
            <label className="label label-danger m-0">Inactive</label>
          )}
        </>
      ),
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Status"),
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="td-actions table-actions-btns custom-send-btn">
          <ul className="list-unstyled mb-0 d-flex justify-content-center">
            {currentUserRole?.editPromoCode ? (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Edit</Tooltip>}
                  placement="left"
                >
                  <Link
                    to={`/Promo/Edit/${row._id}`}
                    type="button"
                    className="btn-link btn-icon btn btn-success "
                  >
                    <FaEdit />
                  </Link>
                </OverlayTrigger>
              </li>
            ) : (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Edit</Tooltip>}
                  placement="left"
                >
                  <Link
                    to={""}
                    type="button"
                    className="btn-link btn-icon btn btn-success disabled"
                    disabled
                  >
                    <FaEdit />
                  </Link>
                </OverlayTrigger>
              </li>
            )}
            {currentUserRole?.deletePromoCode ? (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Delete</Tooltip>}
                  placement="left"
                >
                  <button
                    onClick={() => showModel(row._id)}
                    type="button"
                    className="btn-link btn-icon btn btn-danger"
                  >
                    <FaTrash />
                  </button>
                </OverlayTrigger>
              </li>
            ) : (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Delete</Tooltip>}
                  placement="left"
                >
                  <button
                    type="button"
                    className="btn-link btn-icon btn btn-danger disabled"
                    disabled
                  >
                    <FaTrash />
                  </button>
                </OverlayTrigger>
              </li>
            )}

            {currentUserRole?.editPromoCode ? (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Send Email</Tooltip>}
                  placement="left"
                >
                  <button
                    type="button"
                    className={"btn btn-success custom-set-spacing "}
                    onClick={() => handleSendEmail(row._id)}
                  >
                    Send Email
                  </button>
                </OverlayTrigger>
              </li>
            ) : (
              <li className="d-inline-block align-top">
                <OverlayTrigger
                  overlay={<Tooltip id="tooltip-436082023">Send Email</Tooltip>}
                  placement="left"
                >
                  <button
                    type="button"
                    className={"btn btn-success custom-set-spacing disabled"}
                    disabled
                  >
                    Send Email
                  </button>
                </OverlayTrigger>
              </li>
            )}
          </ul>
        </div>
      ),

      sortable: false,
    },
  ];

  //function to set filter data
  const filterDatatable = (text) => {
    setFilterText(text);
    filterSearchDatatable(selectedOption, text);
  };

  // filter function by name and status
  const filterSearchDatatable = (text, searchText) => {
    const filteredItemsResult = promoCodeData.filter((data) => {
      if (text) {
        if (
          data.status.toString().toLowerCase().includes(text.toLowerCase()) &&
          data?.name.toLowerCase().includes(searchText.toLowerCase())
        ) {
          return data;
        }
      } else if (data?.name.toLowerCase().includes(searchText.toLowerCase())) {
        return data;
      }
      return undefined;
    });
    setFilteredItems([...filteredItemsResult]);
    setSelectedOption(text);
  };

  // function toreset filters
  const resetFilter = () => {
    setFilterText("");
    setFilteredItems([...promoCodeData]);
    setSelectedOption("");
  };

  const toggleFilterAccordion = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // useEffect to refresh promoCode Data
  useEffect(() => {
    if (promoCodeData) {
      setFilteredItems([...promoCodeData]);
    }
  }, [promoCodeData]);

  return (
    <>
      {getDelete ? (
        <SweetAlert
          warning
          showCloseButton
          showCancel
          confirmBtnText="Yes"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="light"
          title="Are you sure you want to delete?"
          onConfirm={deleteMedicalType}
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          You will not be able to recover this data!
        </SweetAlert>
      ) : null}
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3">
          <Container fluid>
            <Row>
              {/* Start of filter container */}
              <Col md="12" className="promo-filter-cards">
                <Card className="filter-card card ">
                  <Card.Header 
                    style={{ cursor: "pointer" }}
                    onClick={toggleFilterAccordion}
                  >
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Filter
                      </Card.Title>
                      <div 
                        style={{ 
                          transform: isFilterOpen ? "rotate(0deg)" : "rotate(180deg)",
                          transition: "transform 0.3s ease"
                        }}
                      >
                        <IoIosArrowUp />
                      </div>
                    </div>
                     
                  </Card.Header>
                  {isFilterOpen && (
                  <Card.Body>
                    <Row>
                      <Col xl={4} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">
                            Search with name...
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="search"
                            placeholder="Search"
                            value={filterText}
                            onChange={(e) => filterDatatable(e.target.value)}
                          ></Form.Control>
                        </Form.Group>
                      </Col>
                      <Col xl={4} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">
                            Filter with status...
                          </Form.Label>
                          <select
                            value={selectedOption}
                            onChange={(e) =>
                              filterSearchDatatable(e.target.value, filterText)
                            }
                          >
                            <option value="">Select Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </Form.Group>
                      </Col>
                      <Col xl={4} md={6}>
                        <Form.Group>
                          <label className="d-none d-sm-block mb-2 form-label">
                            &nbsp;
                          </label>
                          <div className="d-flex  filter-btns-holder">
                            <button
                              type="button"
                              className="outline-button filter-reset-btn"
                              onClick={() => resetFilter()}
                            >
                              <img src={resetBtn} className="img-fluid mr-3" alt="logo"/>
                              Reset
                            </button>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                  )}
                </Card>
                {/* End of filled container */}

                <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                  <h4 className="card-title">Promo Codes</h4>
                  {currentUserRole?.addPromoCode ? (
                    <div className="btn-add">
                      <Link
                        className="float-sm-right btn-filled d-flex align-items-center"
                        to="/promo/Add"
                      >
                        <span className="add-icon mr-2">
                          <FaPlus />
                        </span>{" "}
                        Add Promo Code
                      </Link>
                    </div>
                  ) : (
                    <div className="btn-add">
                      <Button
                        className="float-sm-right btn-filled d-flex align-items-center disabled"
                        disabled
                      >
                        <span className="add-icon mr-2">
                          <FaPlus />
                        </span>{" "}
                        Add Promo Code
                      </Button>
                    </div>
                  )}
                </div>
                <div className="custom-datatable-pagination">
                <DataTable
                  columns={columns}
                  data={filteredItems}
                  theme="solarized"
                  pagination
                  className="react-datatable"
                  progressPending={loader}
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationDefaultPage={currentPage}
                  paginationPerPage={perPage}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default PromoIndex;
